import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { SpellingRepoService } from '@client/app/services/api/spelling-repo/spelling-repo.service';
import { LoadingService } from '@monsido/angular-shared-components';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonUtilsService } from '@client/app/blocks/service/mon-utils/mon-utils.service';

@Component({
    selector: 'table-dictionary-all',
    standalone: false,
    template: `
        <div class="misspellings-multiple-selection-button-container">
            <div class="btn-group mr-1" *ngIf="selected.length" ngbDropdown>
                <button type="button"
                        class="btn btn-border-radius btn-marine-blue"
                        ngbDropdownToggle>
                    {{ 'Action ([[selectedLength]] selected)' | translate:'':{selectedLength: selected.length} }}
                </button>
                <button type="button"
                        class="btn btn-border-radius btn-marine-blue dropdown-toggle"
                        aria-haspopup="true"
                        aria-expanded="false"
                        ngbDropdownToggle>
                    <span class="caret"></span>
                    <span class="sr-only">{{ 'Toggle Dropdown' | translate }}</span>
                </button>
                <ul class="dropdown-menu" ngbDropdownMenu>
                    <li class="cursor-pointer"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'add-to-dictionary-btn'}"
                        monModalConfirm="{{'Are you sure you want to add to the dictionary?' | translate}}"
                        (monModalConfirmAction)="addToDictionary()">
                        <a role="button">
                          <span class="fas fa-book mr-1" aria-hidden="true"></span>
                          <span>{{ 'Add to dictionary' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'add-to-all-language-btn'}"
                        monModalConfirm="{{'Are you sure you want to add to all the dictionaries?' | translate}}"
                        (monModalConfirmAction)="addToAllDictionaries()">
                        <a role="button">
                          <span class="fas fa-atlas mr-1" aria-hidden="true"></span>
                          <span>{{ 'Add to all languages' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        *ngIf="showConfirm"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'confirm-misspelling-btn'}"
                        monModalConfirm="{{'Are you sure you want to confirm this misspelling?' | translate}}"
                        (monModalConfirmAction)="confirmMisspelling()">
                        <a role="button">
                          <span class="fas fa-check mr-1" aria-hidden="true"></span>
                          <span>{{ 'Confirm misspelling' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        *ngIf="!pageId"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'ignore-misspelling-btn'}"
                        monModalConfirm="{{'Are you sure you want to ignore all selected misspellings?' | translate}}"
                        (monModalConfirmAction)="ignoreMisspellings()">
                        <a role="button">
                          <span class="fas fa-atlas mr-1" aria-hidden="true"></span>
                          <span>{{ 'Ignore' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        *ngIf="pageId"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'ignore-misspelling-btn'}"
                        monModalConfirm="{{'Are you sure you want to ignore all selected misspellings?' | translate}}"
                        (monModalConfirmAction)="ignoreMisspelling()">
                        <a role="button">
                          <span class="fas fa-eye-slash" aria-hidden="true"></span>
                          <span>{{ 'Ignore misspelling' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        *ngIf="!pageId"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'markasfix-misspelling-btn'}"
                        [monModalConfirm]="'Are you sure you want to mark this misspelling(s) as fixed?' | translate"
                        (monModalConfirmAction)="markMisspellingsAsFixed()">
                        <a role="button">
                            <span class="fas fa-check mr-1" aria-hidden="true"></span>
                            <span>{{ 'Mark as fixed' | translate }}</span>
                        </a>
                    </li>
                    <li class="cursor-pointer"
                        *ngIf="pageId"
                        monEventTrigger="actionTrigger"
                        [monEventParams]="{action: 'markasfix-misspelling-btn'}"
                        [monModalConfirm]="'Are you sure you want to mark this misspelling(s) as fixed?' | translate"
                        (monModalConfirmAction)="markPageMisspellingsAsFixed()">
                        <a role="button">
                            <span class="fas fa-check" aria-hidden="true"></span>
                            <span>{{ 'Mark as fixed' | translate }}</span>
                        </a>
                    </li>
                </ul>
            </div>

            <button type="button"
                    (click)="clearSelected()"
                    class="btn btn-border-radius btn-secondary clear-section-button-size"
                    *ngIf="selected.length">{{ 'Clear selection' | translate }}</button>
        </div>
    `,
})
export class TableDictionaryAllComponent implements OnInit {
    @Input() showConfirm?: boolean;
    @Input() selected: any[] = [];
    @Input() showIgnore?: boolean;
    @Input() pageId?: any;


    @Output() selectedChange = new EventEmitter<any[]>();
    @Output() callback = new EventEmitter();

    dropdownId: string;
    dialog: any;

    constructor (
        private spellingRepoService: SpellingRepoService,
        private loadingService: LoadingService,
        private translateService: TranslateService,
        private monUtilsService: MonUtilsService,
    ) {
        this.dropdownId = 'tableDictionaryAllDropdown-' + this.monUtilsService.getRandomString();
    }

    ngOnInit (): void {
        this.showConfirm = this.showConfirm || false;
        this.showIgnore = this.showIgnore || false;
    }

    clearSelected (): void {
        this.selected = [];
        this.selectedChange.emit(this.selected);
    }

    addToDictionary (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Adding word to dictionary'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.add({
                    word: value.word,
                    language: value.language,
                    classification: 'dictionary',
                });
            }).catch(() => {});
        });

        this.waitForDone(chain);
    }

    markMisspellingsAsFixed (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Marking as fixed'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.spellingRepoService.destroySpellingError(value.id);
            }).catch(() => {});
        });

        this.waitForDone(chain);
    }

    markPageMisspellingsAsFixed (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Marking as fixed'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.spellingRepoService.destroyPageSpellingError(this.pageId, value.id);
            }).catch(() => {});
        });

        this.waitForDone(chain);
    }

    ignoreMisspelling (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Adding words to ignored list'));

        if (this.pageId) {
            this.selected.forEach((value) => {
                chain = chain.then(() => {
                    return this.ignoreMisspellingForPage(value.id);
                }).catch(() => {});
            });
        }

        this.waitForDone(chain);
    }

    ignoreMisspellingForPage (errorId: any): any {
        return this.spellingRepoService.updatePageIgnoreSpellingError(this.pageId, errorId);
    }

    addToAllDictionaries (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Adding words to dictionary'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.add({
                    word: value.word,
                    classification: 'dictionary',
                });
            });
        });

        this.waitForDone(chain);
    }

    ignoreMisspellings (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Adding words to ignored list'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.add({
                    word: value.word,
                    classification: 'ignore',
                });
            });
        });

        this.waitForDone(chain);
    }

    confirmMisspelling (): void {
        let chain: Promise<any> = Promise.resolve();
        this.dialog = this.loadingService.service(this.translateService.getString('Please wait - Confirming misspelling'));

        this.selected.forEach((value) => {
            chain = chain.then(() => {
                return this.spellingRepoService.confirmWord(value.id, {
                    id: value.id,
                    labels: [],
                });
            }).catch(() => {});
        });

        this.waitForDone(chain);
    }

    waitForDone (promise: Promise<any>): void {
        promise
            .then(() => {
                this.callback.emit();
            })
            .catch(() => {})
            .finally(() => {
                this.dialog.close();
            });
    }

    add (params: any): any {
        return this.spellingRepoService.addToDictionary(params);
    }
}
